<!-- src/components/modals/CreateAssignmentModal.vue -->
<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/50" @click="close" />

      <!-- Modal -->
      <div
        class="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-900">Add Assignment</h2>
          <button
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            @click="close"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" class="text-lg" />
          </button>
        </div>

        <!-- Form -->
        <form class="flex-1 overflow-y-auto p-6 space-y-5" @submit.prevent="handleSubmit">
          <!-- Details -->
          <div class="space-y-4">
            <h3 class="text-sm font-medium text-gray-700">Details</h3>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Assignment Name <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.name"
                type="text"
                required
                placeholder="e.g., Chapter 5 Homework"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                v-model="form.categoryId"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
              >
                <option value="">Select category...</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                  {{ cat.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Marking -->
          <div class="space-y-4 border-t border-gray-200 pt-4">
            <h3 class="text-sm font-medium text-gray-700">Marking Method</h3>

            <div class="grid grid-cols-2 gap-3">
              <button
                v-for="method in markingMethods"
                :key="method.value"
                type="button"
                class="flex items-center gap-3 p-3 border rounded-lg transition-colors text-left"
                :class="
                  form.markingType === method.value
                    ? 'border-bc-indigo-500 bg-bc-indigo-50 ring-2 ring-bc-indigo-500'
                    : 'border-gray-200 hover:border-bc-indigo-300'
                "
                @click="form.markingType = method.value"
              >
                <div
                  class="w-10 h-10 rounded-lg flex items-center justify-center"
                  :class="method.bgColor"
                >
                  <font-awesome-icon :icon="method.icon" :class="method.iconColor" />
                </div>
                <div>
                  <p class="font-medium text-gray-900">{{ method.label }}</p>
                  <p class="text-xs text-gray-500">{{ method.description }}</p>
                </div>
              </button>
            </div>

            <!-- Max points (for points/decimal methods) -->
            <div v-if="showMaxPoints">
              <label class="block text-sm font-medium text-gray-700 mb-1">Maximum Points</label>
              <input
                v-model.number="form.maxPoints"
                type="number"
                min="1"
                placeholder="100"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
              />
            </div>

            <!-- Scale picker (for scale method) -->
            <div v-if="form.markingType === 'SCALE'">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Grading Scale <span class="text-red-500">*</span>
              </label>
              <div v-if="scales.length === 0" class="text-sm text-gray-500 italic py-2">
                No scales configured. Create one in settings first.
              </div>
              <div v-else class="space-y-2">
                <button
                  v-for="scale in scales"
                  :key="scale.id"
                  type="button"
                  class="w-full flex items-center gap-3 p-3 border rounded-lg transition-colors text-left"
                  :class="
                    form.scaleId === scale.id
                      ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-500'
                      : 'border-gray-200 hover:border-purple-300'
                  "
                  @click="form.scaleId = scale.id"
                >
                  <div class="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <font-awesome-icon :icon="['fas', 'a']" class="text-purple-600 text-sm" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="font-medium text-gray-900 text-sm">{{ scale.name }}</p>
                    <p class="text-xs text-gray-500 truncate">
                      {{ scale.levels.map(l => l.text).join(', ') }}
                    </p>
                  </div>
                  <font-awesome-icon
                    v-if="form.scaleId === scale.id"
                    :icon="['fas', 'check-circle']"
                    class="text-purple-600"
                  />
                </button>
              </div>
            </div>
          </div>

          <!-- Dates -->
          <div class="space-y-4 border-t border-gray-200 pt-4">
            <h3 class="text-sm font-medium text-gray-700">Dates</h3>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Date Assigned <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.dateAssigned"
                  type="date"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  v-model="form.dateDue"
                  type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bc-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <!-- Error message -->
          <div
            v-if="error"
            class="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
          >
            {{ error }}
          </div>
        </form>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            class="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
            @click="close"
          >
            Cancel
          </button>
          <button
            :disabled="!canSave || saving"
            class="px-4 py-2 bg-bc-indigo-500 text-white font-medium rounded-lg hover:bg-bc-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            @click="handleSubmit"
          >
            <div
              v-if="saving"
              class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
            />
            <span>{{ saving ? 'Adding...' : 'Add Assignment' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { todayKey } from '@/utils/date'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { gradebookApi, type AssessmentCategory, type Scale } from '@/api'
import { useAnalytics } from '@/composables/useAnalytics'

const props = defineProps<{
  modelValue: boolean
  classId: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  created: []
}>()

const { capture } = useAnalytics()

interface AssignmentForm {
  name: string
  categoryId: string
  markingType: string
  maxPoints: number
  scaleId: string
  dateAssigned: string
  dateDue: string
}

const markingMethods = [
  {
    value: 'INTEGER',
    label: 'Points',
    description: 'Whole numbers',
    icon: ['fas', 'hashtag'],
    bgColor: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  {
    value: 'PERCENT',
    label: 'Percentage',
    description: '0-100%',
    icon: ['fas', 'percent'],
    bgColor: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  {
    value: 'DECIMAL',
    label: 'Decimal',
    description: 'With decimals',
    icon: ['fas', 'calculator'],
    bgColor: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
  },
  {
    value: 'SCALE',
    label: 'Scale',
    description: 'Letter grades',
    icon: ['fas', 'a'],
    bgColor: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
]

// Categories and scales - fetched from API
const categories = ref<AssessmentCategory[]>([])
const scales = ref<Scale[]>([])

const form = ref<AssignmentForm>({
  name: '',
  categoryId: '',
  markingType: 'PERCENT',
  maxPoints: 100,
  scaleId: '',
  dateAssigned: todayKey(),
  dateDue: '',
})

const saving = ref(false)
const error = ref<string | null>(null)

const canSave = computed(() => {
  if (!form.value.name?.trim() || !form.value.dateAssigned || !form.value.markingType) return false
  if (form.value.markingType === 'SCALE' && !form.value.scaleId) return false
  return true
})

const showMaxPoints = computed(() => {
  return form.value.markingType === 'INTEGER' || form.value.markingType === 'DECIMAL'
})

function close() {
  emit('update:modelValue', false)
}

function resetForm() {
  form.value = {
    name: '',
    categoryId: '',
    markingType: 'PERCENT',
    maxPoints: 100,
    scaleId: '',
    dateAssigned: todayKey(),
    dateDue: '',
  }
  error.value = null
}

async function fetchCategories() {
  try {
    categories.value = await gradebookApi.getCategories()
  } catch (err) {
    console.error('Failed to fetch categories:', err)
    categories.value = []
  }
}

async function fetchScales() {
  try {
    scales.value = await gradebookApi.getScales()
  } catch (err) {
    console.error('Failed to fetch scales:', err)
    scales.value = []
  }
}

async function handleSubmit() {
  if (!canSave.value || saving.value) return

  saving.value = true
  error.value = null

  try {
    await gradebookApi.createAssignment(props.classId, {
      assessmentName: form.value.name.trim(),
      categoryId: form.value.categoryId || undefined,
      markingType: form.value.markingType as 'INTEGER' | 'DECIMAL' | 'PERCENT' | 'SCALE',
      maxPoints: form.value.markingType === 'PERCENT' ? 100 : form.value.maxPoints,
      scaleId: form.value.markingType === 'SCALE' ? form.value.scaleId || undefined : undefined,
      weight: 1,
      dateAssigned: `${form.value.dateAssigned}T00:00:00`,
      dateDue: form.value.dateDue ? `${form.value.dateDue}T23:59:59` : undefined,
      excludeFromFinalGrade: false,
    })

    capture('assignment_created')
    emit('created')
    close()
  } catch (err: unknown) {
    console.error('Failed to create assignment:', err)
    error.value = 'Failed to add assignment. Please try again.'
  } finally {
    saving.value = false
  }
}

// Reset form and fetch categories when modal opens
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      resetForm()
      fetchCategories()
      fetchScales()
    }
  }
)
</script>
